import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { Search, Plus, X, Download, Upload } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { makeApi } from "@/lib/api";
import { BulkBar } from "@/components/loads/BulkBar";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { StatCards, CARD_FILTERS } from "@/components/loads/StatCards";
import { LicenseAlerts } from "@/components/drivers/LicenseAlerts";
import { LoadsTable } from "@/components/loads/LoadsTable";
import { DeleteLoadDialog } from "@/components/loads/DeleteLoadDialog";
import { exportLoadsCsv } from "@/lib/csv";
import { BillSelectedDialog } from "@/components/loads/BillSelectedDialog";
import { isBillable } from "@/components/loads/LoadRow";
import { useBulkImport, isRateconFile } from "@/components/import/useBulkImport";
import { ImportProgress } from "@/components/import/ImportProgress";
import { ReviewSheet } from "@/components/import/ReviewSheet";
import { LoadsDropTarget } from "@/components/import/LoadsDropTarget";

const STATUSES = ["All", "New", "In Progress", "Delivered", "Invoiced"];
const STATUS_RANK = { New: 0, "In Progress": 1, Delivered: 2, Invoiced: 3 };
const sortValue = (l, key) => (key === "rate" ? Number(l.rate) : key === "status" ? STATUS_RANK[l.status] : l.created_at || "");

export default function LoadsDashboard() {
  const { getToken } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const navigate = useNavigate();
  const [loads, setLoads] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [card, setCard] = useState(null);
  const [revAllTime, setRevAllTime] = useState(false);
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });
  const [deleting, setDeleting] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [billing, setBilling] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const menuClosedAt = useRef(0);
  const bi = useBulkImport(api);
  const [reviewOpen, setReviewOpen] = useState(false);
  const fileInput = useRef(null);
  const allRead = bi.rows.length > 0 && bi.rows.every((r) => r.phase !== "queued" && r.phase !== "reading");
  useEffect(() => { if (allRead) setReviewOpen(true); }, [allRead]);
  useEffect(() => {
    if (!bi.pending.length) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [bi.pending.length]);
  const addRatecons = (list) => {
    if (![...list].some(isRateconFile)) { toast.error("Drop PDFs or photos of ratecons"); return; }
    bi.addFiles(list);
  };
  const closeReview = useCallback(() => {
    setReviewOpen(false);
    if (!bi.pending.length) bi.reset();
  }, [bi]);

  const refresh = async () => {
    try {
      const [l, d] = await Promise.all([api.get("/loads"), api.get("/drivers")]);
      setLoads(l.data);
      setDrivers(d.data);
    } catch {
      setLoads((p) => p ?? []);
      toast.error("Could not load your loads. Refresh to try again.");
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = (loads || []).filter((l) =>
      (!card || CARD_FILTERS[card](l)) &&
      (statusFilter === "All" || l.status === statusFilter) &&
      (!q || l.load_number.toLowerCase().includes(q) || (l.carrier_number || "").toLowerCase().includes(q) || l.broker_name.toLowerCase().includes(q)));
    const dir = sort.dir === "asc" ? 1 : -1;
    const cmp = (a, b) => {
      const x = sortValue(a, sort.key);
      const y = sortValue(b, sort.key);
      return x > y ? dir : x < y ? -dir : 0;
    };
    return [...rows].sort(cmp);
  }, [loads, query, statusFilter, card, sort]);

  const onSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));
  const openLoad = (l) => { if (Date.now() - menuClosedAt.current > 400) navigate(`/admin/loads/${l.id}`); };
  const onDriverMenu = (open) => { if (!open) menuClosedAt.current = Date.now(); };

  const changeDriver = async (load, driverId) => {
    try {
      const { data } = await api.patch(`/loads/${load.id}`, { assigned_driver_id: driverId });
      setLoads((ls) => ls.map((x) => (x.id === load.id ? { ...x, ...data } : x)));
      toast.success(driverId ? `Load ${load.carrier_number || load.load_number} assigned to ${data.driver_name}` : `Load ${load.carrier_number || load.load_number} unassigned`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change driver");
    }
  };

  const changeStatus = async (load, status) => {
    const prev = load.status;
    setLoads((ls) => ls.map((x) => (x.id === load.id ? { ...x, status } : x)));
    try {
      const { data } = await api.patch(`/loads/${load.id}`, { status });
      setLoads((ls) => ls.map((x) => (x.id === load.id ? { ...x, ...data } : x)));
      toast.success(`Load ${load.carrier_number || load.load_number} set to ${status}`);
    } catch (e) {
      setLoads((ls) => ls.map((x) => (x.id === load.id ? { ...x, status: prev } : x)));
      toast.error(e?.response?.data?.detail || "Could not change status");
    }
  };

  const hasFilters = query || card || statusFilter !== "All";
  const picked = (loads || []).filter((l) => selectedIds.has(l.id));
  const selectedLoads = picked.filter(isBillable);
  const toggle = (ids, on) => setSelectedIds((s) => { const n = new Set(s); ids.forEach((id) => (on ? n.add(id) : n.delete(id))); return n; });
  const plural = (n) => `${n} load${n === 1 ? "" : "s"}`;

  const bulkStatus = async (status) => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/loads/bulk/status", { ids: picked.map((l) => l.id), status });
      toast.success(`${plural(data.updated)} set to ${status}`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change status");
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkDelete = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/loads/bulk/delete", { ids: picked.map((l) => l.id) });
      toast.success(`${plural(data.deleted)} moved to Trash${data.documents_moved ? ` with ${data.documents_moved} document${data.documents_moved === 1 ? "" : "s"}` : ""}`);
      setSelectedIds(new Set());
      setConfirmBulkDelete(false);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete loads");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>Loads</h1>
          <p className="text-sm text-slate-500 mt-1">Tap a row to open the load. <span className="hidden md:inline">Drag ratecons onto the list</span><span className="md:hidden">Upload ratecons</span> to create loads with AI.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input ref={fileInput} type="file" multiple className="hidden" accept="application/pdf,image/*"
            onChange={(e) => { const fs = e.target.files; if (fs?.length) addRatecons([...fs]); e.target.value = ""; }} data-testid="ratecon-file-input" />
          <Button variant="outline" className="rounded-full md:hidden" onClick={() => fileInput.current?.click()} disabled={bi.creating} data-testid="upload-ratecons-btn">
            <Upload className="w-4 h-4 mr-1" /> Upload ratecons
          </Button>
          <Link to="/admin/loads/new">
            <Button className="bg-[#002FA7] hover:bg-[#001A75] rounded-full" data-testid="new-load-btn">
              <Plus className="w-4 h-4 mr-1" /> New load
            </Button>
          </Link>
        </div>
      </div>

      <LicenseAlerts drivers={drivers} link />

      <StatCards loads={loads || []} active={card} allTime={revAllTime} setAllTime={setRevAllTime} onToggle={(k) => { setCard(k); if (k) setStatusFilter("All"); }} />

      <ImportProgress rows={bi.rows} onReview={() => setReviewOpen(true)} onCancel={() => { bi.reset(); setReviewOpen(false); }} />

      <LoadsDropTarget onFiles={addRatecons} disabled={bi.creating}>
      <Card className="border-slate-200">
        <div className="p-4 flex flex-col md:flex-row md:items-center gap-3 border-b border-slate-200">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search by load # or broker…" value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9 pr-9" data-testid="load-search-input" />
            {query && (
              <button onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" aria-label="Clear search" data-testid="clear-search-btn">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="rounded-full shrink-0"
            disabled={!visible.length}
            onClick={() => { exportLoadsCsv(visible, !!hasFilters); toast.success(`Downloaded ${visible.length} load${visible.length === 1 ? "" : "s"}`); }}
            data-testid="download-csv-btn"
          >
            <Download className="w-4 h-4 mr-1" /> {hasFilters ? `Download CSV (${visible.length})` : "Download CSV"}
          </Button>
          <div className="flex gap-2 flex-wrap">
            {STATUSES.map((s) => (
              <button
                key={s}
                onClick={() => { setStatusFilter(s); setCard(null); }}
                data-testid={`filter-${s.toLowerCase().replace(" ", "-")}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${statusFilter === s && !card ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
        <BulkBar count={picked.length} billable={selectedLoads} busy={bulkBusy} onStatus={bulkStatus} onDelete={() => setConfirmBulkDelete(true)} onBill={() => setBilling(true)} onClear={() => setSelectedIds(new Set())} />
        <LoadsTable
          selectedIds={selectedIds}
          onSelect={(l, on) => toggle([l.id], on)}
          onSelectAll={(ls, on) => toggle(ls.map((l) => l.id), on)}
          loads={visible}
          loading={loads === null}
          empty={hasFilters ? "No loads match these filters." : "No loads yet. Create your first one."}
          sort={sort}
          onSort={onSort}
          drivers={drivers}
          onOpen={openLoad}
          onDriverChange={changeDriver}
          onStatusChange={changeStatus}
          onDriverMenu={onDriverMenu}
          onDelete={setDeleting}
        />
      </Card>
      </LoadsDropTarget>

      <ReviewSheet open={reviewOpen} onClose={closeReview} bi={bi}
        onCreated={(n) => { toast.success(`${n} load${n === 1 ? "" : "s"} created`); refresh(); }} />
      <BillSelectedDialog loads={selectedLoads} open={billing} onClose={() => setBilling(false)} onDone={() => { setSelectedIds(new Set()); refresh(); }} />
      <DeleteLoadDialog load={deleting} onClose={() => setDeleting(null)} onDeleted={(id) => { setLoads((ls) => ls.filter((x) => x.id !== id)); toggle([id], false); }} onRestored={refresh} />
      <ConfirmDialog open={confirmBulkDelete} busy={bulkBusy} title={`Delete ${plural(picked.length)}?`}
        description={`${plural(picked.length)} and all their documents (ratecons, PODs, lumper receipts) will move to Trash. You can restore them from Trash for 30 days.`}
        confirmLabel={`Delete ${plural(picked.length)}`} onConfirm={bulkDelete} onCancel={() => setConfirmBulkDelete(false)} testId="bulk-delete-confirm" />
    </div>
  );
}
